import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { Upload } from "lucide-react";
import PlusIcon from "../../../../../assets/icons/PlusIcon";
import Spinner from "../../../../../assets/icons/Spinner";
import FolderPlusIcon from "../../../../../assets/icons/FolderPlusIcon";
import ChevronLeftIcon from "../../../../../assets/icons/ChevronLeftIcon";
import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import backend from "../../../../../backend/backend";
import Notify from "../../../../shared/Notify";
import AssetItem from "./AssetItem";
import FolderAssetItem from "./FolderAssetItem";
import CreateFolderDialog from "./CreateFolderDialog";

import "./FilesContentPanel.scss";

const FilesContentPanel = ({ kbase, workspaceId }) => {
  const intl = useIntl();
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [listCounter, setListCounter] = useState(0);
  const fileInputRef = useRef(null);
  const pollingTimerRef = useRef(null);

  useEffect(() => {
    if (kbase && workspaceId) {
      setLoading(true);
      const folderId = currentFolder?.uid;
      const assetTypes = ["FILE", "FOLDER"]; // Only show files and folders in files area
      backend
        .getKBAssets(workspaceId, kbase.uid, folderId, keyword, assetTypes)
        .then((data) => {
          setAssets(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load assets", err);
          Notify.error(label(intl, "error_load"));
          setLoading(false);
        });
    }
  }, [kbase, workspaceId, keyword, currentFolder, listCounter, intl]);

  // Polling effect for pending/in-progress assets
  useEffect(() => {
    // Clear any existing timer
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    // Check if there are any pending or in-progress assets
    const hasPendingAssets = assets?.some(
      (asset) =>
        asset.ragStatus === "PENDING" || asset.ragStatus === "IN_PROGRESS"
    );

    if (hasPendingAssets && kbase && workspaceId) {
      // Schedule next poll in 5 seconds
      pollingTimerRef.current = setTimeout(() => {
        const folderId = currentFolder?.uid;
        const assetTypes = ["FILE", "FOLDER"];
        backend
          .getKBAssets(workspaceId, kbase.uid, folderId, keyword, assetTypes)
          .then((data) => {
            setAssets(data);
          })
          .catch((err) => {
            console.error("Failed to poll assets", err);
          });
      }, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [assets, kbase, workspaceId, currentFolder, keyword]);

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      // Upload files one by one
      const folderId = currentFolder?.uid;
      for (const file of files) {
        await backend.uploadKBAssetFile(workspaceId, kbase.uid, file, folderId);
      }

      if (files.length === 1) {
        Notify.success(label(intl, "upload_success"));
      } else {
        Notify.success(
          label(intl, "upload_multiple_success").replace(
            "{count}",
            files.length
          )
        );
      }
      setListCounter((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to upload file", err);

      if (err.json) {
        const errorJson = await err.json();
        const errorMsg = errorJson?.message
          ? label(intl, errorJson.message)
          : label(intl, "error_file_upload");
        Notify.error(errorMsg);
      } else {
        Notify.error(label(intl, "error_file_upload"));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFolderClick = (folder) => {
    setFolderStack([...folderStack, currentFolder].filter(Boolean));
    setCurrentFolder(folder);
    setKeyword("");
  };

  const handleBackClick = () => {
    if (folderStack.length > 0) {
      const newStack = [...folderStack];
      const previousFolder = newStack.pop();
      setFolderStack(newStack);
      setCurrentFolder(previousFolder);
    } else {
      setCurrentFolder(null);
    }
    setKeyword("");
  };

  const handleAssetClick = (asset) => {
    if (asset.assetType === "FOLDER" || asset.folder) {
      handleFolderClick(asset);
    } else {
      // TODO: Open asset details dialog or download
      console.log("Asset clicked:", asset);
    }
  };

  const handleAssetDelete = (asset) => {
    backend
      .deleteKBAsset(workspaceId, kbase.uid, asset.uid)
      .then(() => {
        setListCounter((prev) => prev + 1);
      })
      .catch((err) => {
        console.error("Failed to delete asset", err);
        Notify.error(label(intl, "error_delete"));
      });
  };

  const handleAssetDownload = (asset) => {
    backend.downloadKBAssetFile(workspaceId, kbase.uid, asset.uid);
  };

  const onFolderCreated = () => {
    setListCounter((prev) => prev + 1);
  };

  return (
    <div className="files_content_panel">
      <div className="content_header">
        {currentFolder && (
          <div className="breadcrumbs">
            <div onClick={handleBackClick}>
              <ChevronLeftIcon
                size={16}
                strokeWidth="2"
                color="rgb(103, 116, 142)"
              />
              <Message
                id={folderStack.length > 0 ? "back_to_folder" : "back_to_files"}
              />
            </div>
          </div>
        )}
        <div className="header_row">
          <h2>
            {currentFolder && currentFolder.name}
            {!currentFolder && <Message id="kbase_tab_files" />}
          </h2>

          <div className="toolbar_actions">
            <div className="action_buttons">
              <button
                className="btn bg-gradient-primary btn-sm"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? (
                  <Spinner size="18" />
                ) : (
                  <Upload size="18" color="#fff" />
                )}
                <Message id="upload_files" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
              <button
                type="button"
                className="btn btn-outline-dark btn-sm"
                onClick={() => setShowCreateFolder(true)}
              >
                <FolderPlusIcon color="#344767" size="18" />
                <Message id="new_folder" />
              </button>
            </div>
            <div className="right_panel">
              {loading && (
                <div className="loading_container">
                  <Spinner />
                </div>
              )}
              <input
                className="form-control"
                type="text"
                maxLength="255"
                placeholder={label(intl, "search_placeholder")}
                onChange={(e) => setKeyword(e.target.value)}
                value={keyword}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="asset_list_tiles">
        {!assets && <Spinner />}
        {assets && assets.length === 0 && (
          <div className="empty_state">
            <Message id="no_assets_yet" />
          </div>
        )}
        {assets &&
          assets.map((asset) =>
            asset.assetType === "FOLDER" || asset.folder ? (
              <FolderAssetItem
                key={asset.uid}
                asset={asset}
                onClick={() => handleAssetClick(asset)}
                onDelete={handleAssetDelete}
              />
            ) : (
              <AssetItem
                key={asset.uid}
                asset={asset}
                onClick={() => handleAssetClick(asset)}
                onDelete={handleAssetDelete}
                onDownload={handleAssetDownload}
              />
            )
          )}
      </div>

      {showCreateFolder && (
        <CreateFolderDialog
          workspaceId={workspaceId}
          kbaseId={kbase.uid}
          folderId={currentFolder?.uid}
          onClose={() => setShowCreateFolder(false)}
          onSuccess={onFolderCreated}
        />
      )}
    </div>
  );
};

export default FilesContentPanel;
